import React, { useEffect, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  OutlinedInput,
  Box,
} from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';

/**
 * Props:
 *   selectedOutput:    string (currently chosen output deviceId)
 *   setSelectedOutput: (deviceId: string) => void
 *
 * Behavior:
 *   1. On mount, we request a generic getUserMedia({ audio: true }) so that:
 *      • The browser will prompt for mic-permission (labels for all devices—including outputs—become available).
 *      • We can then call enumerateDevices() and see each audiooutput’s label.
 *   2. After enumeration, we look for a device with deviceId==="default" (this represents the OS/browser default speaker),
 *      or fall back to the first audiooutput in the list. We call setSelectedOutput(defaultOutputId) if the parent hasn’t already set a value.
 *   3. When the user picks a different output from the dropdown, we simply update selectedOutput via setSelectedOutput(newId).
 *      (Note: Changing an output device does not require a new getUserMedia call. To actually route audio to the new sink,
 *       the parent component can pass that deviceId into HTMLMediaElement.setSinkId() or another API.)
 */
export default function AudioOutputDeviceSelector({
  selectedOutput,
  setSelectedOutput,
}) {
  const [outputDevices, setOutputDevices] = useState([]);

  useEffect(() => {
    async function fetchOutputDevices() {
      // 1. Ask for mic-permission so that device labels (including audiooutput) are populated
      try {
        const tempStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        tempStream.getTracks().forEach((t) => t.stop());
      } catch (err) {
        console.warn(
          'User denied microphone access. Output device labels may be hidden.',
          err
        );
      }

      // 2. Enumerate all devices, then filter for audiooutput
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const audioOutputs = allDevices.filter((d) => d.kind === 'audiooutput');
      setOutputDevices(audioOutputs);

      // 3. Set the default output device to the first one
      const defaultOutput = audioOutputs.length ? audioOutputs[0] : null;

      // 4. If parent hasn’t already picked an output, set it to default
      if (defaultOutput && !selectedOutput) {
        // Use setTimeout to ensure this runs after the component has mounted
        setSelectedOutput(defaultOutput.deviceId);
      }
    }

    fetchOutputDevices();
    // We only run this once on mount.
  }, []);

  const handleOutputChange = (event) => {
    const newDeviceId = event.target.value;
    setSelectedOutput(newDeviceId);
  };

  if (outputDevices.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ width: 300 }}
      >
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="output-device-label">Output Device</InputLabel>
          <OutlinedInput
            value={'Loading output devices ...'}
            disabled={true}
            startAdornment={
              <InputAdornment position="start">
                <VolumeUpIcon />
              </InputAdornment>
            }
          />
        </FormControl>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{ width: 250 }} // Adjust width as needed
    >
      <FormControl variant="outlined" fullWidth>
        <InputLabel id="output-device-label">Output Device</InputLabel>

        <Select
          labelId="output-device-label"
          id="output-device-select"
          value={selectedOutput || ''}
          onChange={handleOutputChange}
          label="Output Device"
          input={
            <OutlinedInput
              startAdornment={
                <InputAdornment position="start">
                  <VolumeUpIcon />
                </InputAdornment>
              }
              notched
            />
          }
        >
          {outputDevices.map((device) => (
            <MenuItem key={device.deviceId} value={device.deviceId}>
              {device.label || 'Unknown Speaker'}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}
